"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRightIcon, PlusIcon } from "@radix-ui/react-icons";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import type { RestaurantSiteConfig } from "@/lib/restaurant-platform";
import type { HomepageFeaturedMenuItem } from "@/lib/homepage-menu-preview";

type OwnerFeaturedRowProps = {
  siteConfig: RestaurantSiteConfig;
  items: HomepageFeaturedMenuItem[];
};

function isExternalHref(href: string): boolean {
  return /^https?:\/\//.test(href);
}

function externalLinkProps(href: string) {
  return isExternalHref(href)
    ? { target: "_blank", rel: "noopener noreferrer" }
    : {};
}

function menuItemHref(siteConfig: RestaurantSiteConfig, item: HomepageFeaturedMenuItem) {
  if (siteConfig.menuUrl !== "/menu") {
    return siteConfig.menuUrl;
  }

  if (!item.id) {
    return item.href;
  }

  const [pathname, hash = ""] = item.href.split("#");
  const params = new URLSearchParams();
  params.set("item", item.id);
  params.set("itemName", item.name);

  return `${pathname}?${params.toString()}${hash ? `#${hash}` : ""}`;
}

export function OwnerFeaturedRow({
  siteConfig,
  items,
}: OwnerFeaturedRowProps) {
  const featuredItems = items.slice(0, 9);
  const scrollerRef = React.useRef<HTMLUListElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(true);

  const updateScrollState = React.useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft < maxScroll - 8);
  }, []);

  React.useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState]);

  const scrollByCard = React.useCallback((direction: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("li");
    const step = card
      ? card.getBoundingClientRect().width + 16
      : el.clientWidth * 0.8;
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  }, []);

  return (
    <section
      aria-labelledby="owner-featured-heading"
      className="bg-white px-4 py-14 md:px-6 md:py-20"
    >
      <div className="mx-auto max-w-7xl">
        <div className="flex items-center justify-between gap-4">
          <h2
            id="owner-featured-heading"
            className="font-system text-[clamp(1.625rem,4vw,2rem)] font-bold leading-tight tracking-tight text-[#1a1a1a]"
          >
            Featured
          </h2>
          <div className="flex items-center gap-3 sm:gap-4">
            <Link
              href={siteConfig.menuUrl}
              prefetch={false}
              {...externalLinkProps(siteConfig.menuUrl)}
              data-track="menu_clicked"
              data-track-location="home_owner_featured_viewmenu"
              className="font-system inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-neutral-500 transition-colors hover:text-[#1a1a1a] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f45c24]"
            >
              View menu
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </Link>
            {/* Carousel controls: visible from sm up; on touch widths the
                cut-off peek card + edge fade carry the "this scrolls" cue. */}
            <div className="hidden items-center gap-2 sm:flex">
              <button
                type="button"
                aria-label="Scroll featured items back"
                onClick={() => scrollByCard(-1)}
                disabled={!canScrollLeft}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-neutral-200 bg-white text-[#1a1a1a] transition-colors hover:border-neutral-300 hover:bg-neutral-50 disabled:cursor-default disabled:opacity-35 disabled:hover:border-neutral-200 disabled:hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f45c24]"
              >
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label="Scroll featured items forward"
                onClick={() => scrollByCard(1)}
                disabled={!canScrollRight}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-neutral-200 bg-white text-[#1a1a1a] transition-colors hover:border-neutral-300 hover:bg-neutral-50 disabled:cursor-default disabled:opacity-35 disabled:hover:border-neutral-200 disabled:hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f45c24]"
              >
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        <div className="relative -mx-4 mt-6 sm:-mx-8 sm:mt-8">
          <ul
            ref={scrollerRef}
            aria-label="Featured Tuscan Cove menu items"
            className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory px-4 pb-2 scroll-pl-4 sm:px-8 sm:scroll-pl-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {featuredItems.map((item) => {
              const cardHref = menuItemHref(siteConfig, item);

              return (
                <li
                  key={item.displayName}
                  className="snap-start shrink-0 w-[clamp(13.5rem,62vw,17.5rem)] sm:w-[17.5rem]"
                >
                  <Link
                    href={cardHref}
                    prefetch={false}
                    {...externalLinkProps(cardHref)}
                    data-track="menu_clicked"
                    data-track-location="home_owner_featured"
                    data-track-label={item.displayName}
                    className="group block rounded-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f45c24]"
                  >
                    <div className="relative aspect-square overflow-hidden rounded-xl bg-neutral-100">
                      <Image
                        src={item.mobilePhotoUrl ?? item.photoUrl}
                        alt={`${item.displayName} at Tuscan Cove`}
                        fill
                        sizes="(max-width: 640px) 68vw, 280px"
                        quality={72}
                        className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                      <span
                        aria-hidden="true"
                        className="absolute bottom-3 right-3 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#1a1a1a] shadow-[0_3px_12px_-3px_rgba(0,0,0,0.3)] transition-colors duration-200 group-hover:bg-[#f45c24] group-hover:text-[#1a1a1a]"
                      >
                        <PlusIcon className="h-5 w-5" />
                      </span>
                    </div>
                    <p className="font-system mt-3 text-[15px] font-medium leading-snug text-[#1a1a1a]">
                      {item.displayName}
                    </p>
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* Edge fades signal overflow in both directions. */}
          <div
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-white to-transparent transition-opacity duration-300 sm:w-14",
              canScrollLeft ? "opacity-100" : "opacity-0",
            )}
          />
          <div
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent transition-opacity duration-300 sm:w-14",
              canScrollRight ? "opacity-100" : "opacity-0",
            )}
          />
        </div>
      </div>
    </section>
  );
}

export default OwnerFeaturedRow;
